import {ArrowDownTrayIcon, CheckCircleIcon, XCircleIcon} from "@heroicons/react/24/solid";
import {ImportResult} from "../../../models/ImportResult";

interface ImportResultsProps {
    result: ImportResult;
    onClose: () => void;
}

export default function ImportResults({result}: ImportResultsProps) {

    // CSV generation functions
    const generateSuccessfulCSV = (): string => {
        const headers = [
            'Spreadsheet Row',
            'Filename',
            'Newspaper Name',
            'Newspaper City',
            'Newspaper State',
            'Year',
            'Month',
            'Day',
            'Page',
            'Publication Date',
            'Database Advertisement ID'
        ];

        const csvContent = [
            headers.join(','),
            ...result.successful.map(item => [
                item.spreadsheetRow,
                `"${item.filename || ''}"`,
                `"${item.newspaperName || ''}"`,
                `"${item.newspaperCity || ''}"`,
                `"${item.newspaperState || ''}"`,
                `"${item.year || ''}"`,
                `"${item.month || ''}"`,
                `"${item.day || ''}"`,
                `"${item.page || ''}"`,
                `"${item.publicationDate || ''}"`,
                `"${item.processedImportAdvertisementID || ''}"`
            ].join(','))
        ].join('\n');

        return csvContent;
    };

    const generateFailedCSV = (): string => {
        const headers = [
            'Spreadsheet Row',
            'Filename',
            'Newspaper Name',
            'Newspaper City',
            'Newspaper State',
            'Year',
            'Month',
            'Day',
            'Page',
            'Publication Date',
            'Validation Messages'
        ];

        const csvContent = [
            headers.join(','),
            ...result.failed.map(item => [
                item.spreadsheetRow,
                `"${item.filename || ''}"`,
                `"${item.newspaperName || ''}"`,
                `"${item.newspaperCity || ''}"`,
                `"${item.newspaperState || ''}"`,
                `"${item.year || ''}"`,
                `"${item.month || ''}"`,
                `"${item.day || ''}"`,
                `"${item.page || ''}"`,
                `"${item.publicationDate || ''}"`,
                `"${(item.validationMessages || []).join('; ')}"`
            ].join(','))
        ].join('\n');

        return csvContent;
    };

    const generateErrorsCSV = (): string => {
        const headers = ['Error Message'];

        const csvContent = [
            headers.join(','),
            ...result.errors.map(error => `"${error}"`)
        ].join('\n');

        return csvContent;
    };

    // Download function
    const downloadCSV = (content: string, filename: string) => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadSuccessful = () => {
        const csv = generateSuccessfulCSV();
        downloadCSV(csv, `successful-records-${result.importID}.csv`);
    };

    const handleDownloadFailed = () => {
        const csv = generateFailedCSV();
        downloadCSV(csv, `failed-records-${result.importID}.csv`);
    };

    const handleDownloadErrors = () => {
        const csv = generateErrorsCSV();
        downloadCSV(csv, `system-errors-${result.importID}.csv`);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Import Results
                        </h2>

                    </div>
                </div>

                {/* Status Overview */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            result.completed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                            {result.completed ? (
                                <CheckCircleIcon className="h-4 w-4 mr-1"/>
                            ) : (
                                <XCircleIcon className="h-4 w-4 mr-1"/>
                            )}
                            {result.completed ? 'Completed' : 'Failed'}
                        </div>

                    </div>
                </div>

                {/* Main Content */}
                <div className="px-6 py-6">
                    <div className="space-y-6">
                        {/* Overview Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">{result.successful.length}</div>
                                <div className="text-sm text-green-800">Records Processed Successfully</div>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-red-600">{result.failed.length}</div>
                                <div className="text-sm text-red-800">Records Failed</div>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-red-600">{result.errors.length}</div>
                                <div className="text-sm text-red-800">System Errors</div>
                            </div>
                        </div>

                        {/* Status Message */}
                        {!result.completed && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                <div className="flex">
                                    <div className="text-sm text-yellow-700">
                                        <p className="font-medium">Import was not completed successfully.</p>
                                        <p>Download the CSV files below to review the details.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Download Section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900">Download Reports</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Successful Records Download */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900">Successful Records</h4>
                                            <p className="text-sm text-gray-500">{result.successful.length} records</p>
                                        </div>
                                        <button
                                            onClick={handleDownloadSuccessful}
                                            disabled={result.successful.length === 0}
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ArrowDownTrayIcon className="h-4 w-4 mr-1"/>
                                            CSV
                                        </button>
                                    </div>
                                </div>

                                {/* Failed Records Download */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900">Failed Records</h4>
                                            <p className="text-sm text-gray-500">{result.failed.length} records</p>
                                        </div>
                                        {result.failed.length > 0 && <button
                                            onClick={handleDownloadFailed}
                                            disabled={result.failed.length === 0}
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ArrowDownTrayIcon className="h-4 w-4 mr-1"/>
                                            CSV
                                        </button>}
                                    </div>
                                </div>

                                {/* System Errors Download */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900">System Errors</h4>
                                            <p className="text-sm text-gray-500">{result.errors.length} errors</p>
                                        </div>
                                        {result.errors.length > 0 && <button
                                            onClick={handleDownloadErrors}
                                            disabled={result.errors.length === 0}
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ArrowDownTrayIcon className="h-4 w-4 mr-1"/>
                                            CSV
                                        </button>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-end space-x-3">
                        {result.completed && (
                            <button
                                onClick={() => window.location.replace("/imports")}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                            >
                                Return to Imports
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}